import { isError } from 'lodash/fp'

import {
  getCustomerEndpoint,
  TFetchTokenParams,
  TFetchUserTokenParams,
} from '~config/serverEndpoints'

type TCommonFetchTokenOptions = {
  backendUrl: string
  headers?: RequestInit['headers']
}

type TFetchTokenOptions = TCommonFetchTokenOptions & {
  params: TFetchTokenParams
}

export type TFetchTokenResponse = {
  userId: number
  token: string
}

const DEFAULT_MESSAGE = `Authorization failed`

// common logic for fetching the user info (registered or a guest)
const fetchToken = async ({
  params,
  backendUrl,
  headers,
}: TFetchTokenOptions): Promise<TFetchTokenResponse> => {
  try {
    const response = await window.fetch(getCustomerEndpoint(backendUrl, params), { headers })
    const result = await response.json()

    if (result.error) {
      if (isError(result.error)) throw result.error
      if (typeof result.error === `string`) throw new Error(result.error)

      throw new Error(DEFAULT_MESSAGE)
    }

    return result
  } catch (err) {
    const error = isError(err) ? err : new Error(DEFAULT_MESSAGE)

    throw error
  }
}

/**
 * fetches the basic info of a guest user from the server endpoint
 */
export const fetchTokenForGuest = async ({ backendUrl, headers }: TCommonFetchTokenOptions) => {
  return fetchToken({ backendUrl, params: { guest: true }, headers })
}

/**
 * fetches the basic info of a registered user from the server endpoint
 * using user’s login & password as params
 * can be used to verify that the user exists as well
 */
export const fetchTokenWithPassword = async ({
  username,
  password,
  backendUrl,
  headers,
}: TCommonFetchTokenOptions & TFetchUserTokenParams) => {
  return fetchToken({ backendUrl, params: { username, password }, headers })
}
